import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import './Absences.css';

const Absences = () => {
    const [absences, setAbsences] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [isRefreshing, setIsRefreshing] = useState(false);

    const API_BASE = process.env.REACT_APP_API_BASE;

    const fetchAbsences = useCallback(async (showSpinner = true) => {
        if (showSpinner) setLoading(true);
        setError('');
        
        try {
            const token = localStorage.getItem('discord_token');
            if (!token) {
                setError('Nicht autorisiert. Bitte erneut anmelden.');
                return;
            }

            const response = await axios.get(`${API_BASE}/api/absences`, {
                headers: { Authorization: `Bearer ${token}` },
                timeout: 10000 // Timeout nach 10 Sek
            });
            
            // Sortierung: Aktive zuerst, dann nach Startdatum
            const sortedData = response.data.sort((a, b) => 
                new Date(b.start_date) - new Date(a.start_date)
            );
            
            setAbsences(sortedData);
        } catch (err) {
            console.error('Fetch Error:', err);
            setError(err.response?.data?.message || 'Abwesenheiten konnten nicht geladen werden.');
        } finally {
            setLoading(false);
            setIsRefreshing(false);
        }
    }, [API_BASE]);

    useEffect(() => {
        fetchAbsences();
    }, [fetchAbsences]);

    // Statistiken berechnen (optimiert mit useMemo)
    const stats = useMemo(() => {
        const now = new Date();
        const active = absences.filter(a => new Date(a.end_date) >= now);
        return {
            total: absences.length,
            activeCount: active.length
        };
    }, [absences]);

    const handleRefresh = () => {
        setIsRefreshing(true);
        fetchAbsences(false);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <div className="admin-panel-container center-content">
                <div className="spinner"></div>
                <p>Abwesenheiten werden geladen...</p>
            </div>
        );
    }

    return (
        <div className="absence-container">
            {/* Enhanced Header */}
            <div className="header-section">
                <div className="header-content">
                    <div className="icon-container">
                        <div className="icon-badge">👑</div>
                    </div>
                    <div className="title-section">
                        <h1 className="main-title">
                            TEAM<span className="title-accent">ABWESENHEITEN</span>
                        </h1>
                        <p className="subtitle">Übersicht aller abgemeldeten Teammitglieder</p>
                        <div className="stats-row">
                            <div className="stat-item">
                                <span className="stat-number">{stats.total}</span>
                                <span className="stat-label">GESAMT</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-number active">{stats.activeCount}</span>
                                <span className="stat-label">AKTIV</span>
                            </div>
                        </div>
                    </div>
                    <button
                        className={`cyber-btn refresh ${isRefreshing ? 'spinning' : ''}`}
                        onClick={handleRefresh}
                        disabled={isRefreshing}
                        title="Aktualisieren"
                    >
                        <span className="btn-icon">🔄</span>
                    </button>
                </div>
            </div>

            {error && (
                <div className="status-notification error">
                    <div className="notification-icon">❌</div>
                    <div className="notification-content">
                        <div className="notification-title">Fehler</div>
                        <div className="notification-message">{error}</div>
                    </div>
                </div>
            )}

            {absences.length === 0 && !error ? (
                <div className="empty-state">
                    <div className="empty-icon">🏖️</div>
                    <h3>Alle im Dienst</h3>
                    <p>Aktuell sind alle Teammitglieder verfügbar und im Einsatz.</p>
                    <div className="empty-hint">Neue Abwesenheiten werden hier angezeigt</div>
                </div>
            ) : (
                <table className="absences-table">
                    <thead>
                        <tr>
                            <th>Benutzer</th>
                            <th>Status</th>
                            <th>Startdatum</th>
                            <th>Enddatum</th>
                            <th>Grund</th>
                            <th>Eingetragen</th>
                        </tr>
                    </thead>
                    <tbody>
                        {absences.map((absence) => {
                            const isActive = new Date(absence.end_date) >= new Date();

                            return (
                                <tr key={absence.id} className={isActive ? 'active' : 'past'}>
                                    <td>
                                        <div className="user-info">
                                            <div className="user-avatar-container">
                                                {absence.avatar_url ? (
                                                    <img src={absence.avatar_url} alt="" className="avatar-img" />
                                                ) : (
                                                    <div className="user-avatar">👤</div>
                                                )}
                                            </div>
                                            <span className="user-name">{absence.user_name}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <span className={`status-indicator ${isActive ? 'active' : 'past'}`}>
                                            {isActive ? '🔴 LIVE - Abwesend' : '⚪ BEENDET'}
                                        </span>
                                    </td>
                                    <td>{formatDate(absence.start_date)}</td>
                                    <td>{formatDate(absence.end_date)}</td>
                                    <td>{absence.reason || 'Kein Grund angegeben'}</td>
                                    <td>{formatDate(absence.created_at)}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}
        </div>
    );
};

export default Absences;
